import {
    BackgroundTypes,
    YouTubeCampaignData,
    BrandContentExistsData, CampaignDetails, CampaignPreSaveCreationData, CurrencyCodesType, CurrentBrand, PlaylistDatav2, StyleTypes, TitleAlignments, PresaveCampaignExistsData, FacebookCampaignInput, CreatedCampaign, DuplicateSongAdsRecord, EmailCampaignData, EmailCampaignDetailsData, GrowInstagramCampaignInput, LinkClicksCampaignInput, PlaylistingCampaignInput,
    StreamingPlatformsData,
    StreamsData,
    MediaType,
    FBLocation,
    InstagramPost
} from 'modules/Types';
import Axios from 'helpers/Interceptor';
import Logger from 'Logger';
import * as Sentry from "@sentry/react"
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import { CampaignFilterType } from 'pages/MarketingPage/Components/CampaignTypesFilter/CampaignFilterTypes';
import { CampaignMapping } from 'modules/Types';
import { FacebookCampaignSubmissionType, TikTokCampaignSubmissionType, PlaylistingCampaignSubmissionType } from 'modules/Campaign';
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView';
import { StatItem } from 'elements/Stat';
import { CreativeSelectorPost } from 'Hooks/useCampaignBuilder';
import { ImportFromURLResponse } from './agentApi';

/**
 * Forces a presave release for a given content
 * @param contentSlug - Slug identifier for the content
 * @param spotifyId - Optional Spotify ID
 * @returns Promise containing the force release response or null if error
 */
export const forceRelease_api = async (
    contentSlug: string,
    spotifyId?: string
): Promise<any | null> => {
    try {
        const response = await Axios.post<any>(
            `/campaign/presave/${contentSlug}/force-release`,
            { spotifyId }
        );

        return response.data;
    } catch (error) {
        console.error('Failed to force release:', error);
        return null;
    }
}

/**
 * Fetches campaign details from the API
 * @param campaignId - The ID of the campaign to fetch
 * @returns Promise containing campaign details or null if not found/error
 */
export const getCampaign_api = async (
    campaignId: number
): Promise<CampaignDetails | null> => {
    if (!campaignId) {
        return null;
    }

    try {
        const response = await Axios.get<CampaignDetails>(
            `/campaign/${campaignId}`
        );
        return response.data;
    } catch (error) {
        console.error('Failed to fetch campaign:', error);
        return null;
    }
}

/** Parameters for fetching dashboard campaign data */
interface GetDashboardCampaignsProps {
    /** Brand ID to fetch campaigns for */
    brandId: string | number;
    /** Start date in YYYY-MM-DD format */
    startDate: string;
    /** End date in YYYY-MM-DD format */
    endDate: string;
    /** Date modifier for filtering - defaults to 'in_between' */
    dateModifier?: string;
    /** Number of days to filter by - defaults to 30 */
    filterDays?: number;
}

/** Response structure for dashboard campaigns */
export interface DashboardCampaigns {
    /** Array of campaign data */
    campaigns: CampaignDetails[];
    /** Any additional metadata or summary stats */
    meta?: {
        total?: number;
        page?: number;
        perPage?: number;
    };
}

/**
 * Fetches dashboard campaign data for a specific brand within a date range
 * @param props - Parameters for the campaign data request
 * @returns Promise containing the campaign dashboard data or null if error
 */
export const getDashboardCampaigns_api = async ({
    brandId,
    startDate,
    endDate,
    dateModifier = 'in_between',
    filterDays = 30
}: GetDashboardCampaignsProps): Promise<DashboardCampaigns | null> => {
    try {
        const response = await Axios.get<DashboardCampaigns>(
            `/campaign/brand/${brandId}`,
            {
                params: {
                    dateModifier,
                    startDate,
                    endDate,
                    filterDays
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error('Failed to fetch dashboard campaigns:', error);
        return null;
    }
}

/** Response structure for Facebook ad media */
interface FacebookAdMedia {
    /** URL of the media asset */
    media_url: string;
    /** Owner information of the media */
    owner: string;
    /** Shareable preview link */
    preview_shareable_link: string;
    /** URL of the thumbnail image */
    thumbnail_url: string;
}

/** Parameters for updating Facebook ad media */
interface UpdateFacebookAdMediaProps {
    /** Campaign ID to update */
    campaignId: string | number;
    /** Associated brand ID */
    brandId: string | number;
    /** Facebook access token */
    accessToken: string;
}

/**
 * Updates Facebook ad media for a campaign
 * @param props - Parameters for updating Facebook ad media
 * @returns Promise containing the updated media details or null if error
 */
export const updateFacebookAdMedia_api = async ({
    campaignId,
    brandId,
    accessToken
}: UpdateFacebookAdMediaProps): Promise<FacebookAdMedia | null> => {
    if (!campaignId || !brandId || !accessToken) {
        return null;
    }

    try {
        const response = await Axios.put<FacebookAdMedia>(
            `/campaign/${campaignId}/facebook-ad-media`,
            {
                brandId,
                access_token: accessToken
            }
        );

        return response.data;
    } catch (error) {
        console.error('Failed to update Facebook ad media:', error);
        return null;
    }
}

/**
 * Fetches campaigns associated with a specific brand
 * @param brandId - The ID of the brand to fetch campaigns for 
 * @param params - Filter parameters for the campaigns
 * @returns Promise containing array of campaign details or empty array if error
 */
export const getCampaignsByBrandId_api = async ({
    brandId,
    params,
}: {
    brandId: number;
    params: CampaignFilterType;
}): Promise<CampaignDetails[]> => {
    if (!brandId) {
        return [];
    }

    try {
        const url = `/campaign/brand/${brandId}`
        const response = await Axios.get<CampaignDetails[]>(url, {
            params
        });

        return response.data || [];
    } catch (error) {
        console.error('Failed to fetch campaigns for brand:', error);
        return [];
    }
}

/** Parameters for finalizing a campaign */
interface FinalizeCampaignProps {
    /** ID of the campaign to finalize */
    campaignId: string | number;
    /** ID of the brand associated with the campaign */
    brandId: string | number;
    /** Stripe session ID */
    sessionId: string;
}

/** Parameters for downloading a campaign report */
interface DownloadCampaignReportProps {
    /** ID of the brand associated with the campaign */
    brandId: number;
    /** ID of the campaign to download report for */
    campaignId: number;
}

/**
 * Downloads a PDF report for a specific campaign
 * @param props - Parameters for downloading the campaign report
 * @returns Promise containing the PDF blob data or null if error
 */
export const downloadCampaignReport_api = async ({
    brandId,
    campaignId
}: DownloadCampaignReportProps): Promise<Blob | null> => {
    try {
        const response = await Axios.get<Blob>(`/campaign/${campaignId}/export-pdf`, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            },
            responseType: 'blob',
            timeout: 120000
        });

        return response.data;
    } catch (error) {
        console.error('Failed to download campaign report:', error);
        return null;
    }
}

/**
 * Deletes a campaign by its ID
 * @param campaignId - The ID of the campaign to delete
 * @returns Promise that resolves when deletion is successful, rejects on error
 */
export const deleteCampaign_api = async (campaignId: number): Promise<void> => {
    if (!campaignId) {
        throw new Error('Campaign ID is required');
    }

    try {
        await Axios.delete(`/campaign/${campaignId}`);
    } catch (error) {
        console.error('Failed to delete campaign:', error);
        throw error;
    }
}

/**
 * Removes the error from a campaign
 * @param campaign - The campaign to remove the error from with the structure of a campaign on database
 */
export const removeCampaignError_api = async (campaign: CampaignDetails) => {
    try {
        await Axios.put(`/campaign/${campaign.id}`, {
            ...campaign,
            campaign_metadata: {
                ...campaign.campaign_metadata,
                error: null
            }
        });
    } catch (error) {
        console.error('Failed to remove campaign error:', error);
        throw error;
    }
}

/** Parameters for updating campaign release date */
export interface UpdateReleaseDateProps {
    /** Campaign ID to update */
    campaignId: number;
    /** New release date in UTC format */
    releaseDate: string;
    /** Timezone for the release */
    timezone: string | null;
    /** Whether the release is local */
    localRelease: boolean;
    /** Whether to hide the release date */
    hideReleaseDate: boolean;
    /** Whether the campaign can be edited (for in_progress/not_found statuses) */
    canEdit?: boolean;
}

/**
 * Updates the release date settings for a campaign
 * @param props - Parameters for updating the release date
 * @returns Promise containing the updated release date data or throws error
 */
export const updateCampaignReleaseDate_api = async (
    props: UpdateReleaseDateProps
): Promise<UpdateReleaseDateProps> => {
    const { campaignId, ...updateData } = props;

    try {
        const response = await Axios.put<any>(
            `/campaign/${campaignId}/presave/releaseDate`,
            updateData
        );

        if (!response?.data || response.isError) {
            throw new Error('Error updating campaign release date');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update campaign release date:', error);
        throw error;
    }
}

/** Parameters for pausing/resuming a campaign */
interface UpdateCampaignPauseStatusProps {
    /** Campaign ID to update */
    campaignId: number;
    /** Whether to pause (true) or resume (false) the campaign */
    pause: boolean;
    /** Optional Facebook access token */
    accessToken?: string | null;
    /** Whether to pause all assets (true) or not (false) */
    allAssets?: boolean;
}

/**
 * Updates the pause status of a campaign
 * @param props - Parameters for updating campaign pause status
 * @returns Promise containing the updated campaign data or throws error
 */
export const updateCampaignPauseStatus_api = async ({
    campaignId,
    pause,
    allAssets
}: UpdateCampaignPauseStatusProps): Promise<CampaignDetails> => {
    try {
        const response = await Axios.put<CampaignDetails>(
            `/campaign/${campaignId}/pause/v2`,
            {
                pause,
                allAssets
            },
            { timeout: 30000 }
        );

        if (!response?.data || response.isError) {
            throw new Error('Error updating campaign pause status');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update campaign pause status:', error);
        throw error;
    }
}

/** Response structure for campaign status information */
interface CampaignStatusInfo {
    /** Unique identifier for the campaign */
    id: string;
    /** Name of the campaign */
    name: string;
    /** Current status of the campaign */
    effective_status: string;
    /** Array of issues if any exist */
    issues_info?: Array<{
        /** Error code for the issue */
        error_code: string;
        /** Level at which the issue occurs */
        level: string;
        /** Detailed message about the issue */
        message: string;
    }>;
}

/** Parameters for fetching campaign status information */
interface GetCampaignStatusesProps {
    /** Brand ID to fetch campaign statuses for */
    brandId: string | number;
    /** Array of campaign IDs to fetch statuses for */
    campaignIds: string[];
    /** Optional limit for number of results */
    limit?: number | null;
}

/**
 * Fetches status information for specified campaigns
 * @param props - Parameters for fetching campaign statuses
 * @returns Promise containing array of campaign status info or empty array if error
 */
export const getFacebookCampaignStatuses_api = async ({
    brandId,
    campaignIds,
    limit = null
}: GetCampaignStatusesProps): Promise<CampaignStatusInfo[]> => {
    try {
        const response = await Axios.get<CampaignStatusInfo[]>(
            `/campaign/brand/${brandId}/facebook/info`,
            {
                params: {
                    fields: 'effective_status,id,name,issues_info',
                    ids: campaignIds,
                    type: 'campaigns',
                    limit
                }
            }
        );

        return response.data.filter((campaign: CampaignStatusInfo) => campaign.id) || [];
    } catch (error) {
        console.error('Failed to fetch campaign statuses:', error);
        return [];
    }
}

/**
 * Converts an amount from one currency to another
 * @param {Object} params - The conversion parameters
 * @param {CurrencyCodesType} params.from - Source currency code
 * @param {CurrencyCodesType} params.to - Target currency code
 * @param {number} params.amount - Amount to convert
 * @returns {Promise<number>} The converted amount, or original amount if currencies are the same
 */
export const getConvertedCurrency_api = async ({ from, to, amount }: { from: CurrencyCodesType, to: CurrencyCodesType, amount: number }) => {
    if (from === to) {
        return amount;
    }

    const url = `/campaign/convert/${from}/${to}/${amount}`
    try {
        const response = await Axios.get<{ amountConverted: number }>(url);
        return response.data.amountConverted;
    } catch (error) {
        Logger.error(error);
        return amount;
    }
}

/**
 * Updates the status of a campaign
 * @param data - Object containing campaign update data
 * @param data.campaignId - ID of the campaign to update
 * @param data.status - New status to set for the campaign
 * @returns Promise containing the updated campaign data with new status
 * @throws Error if update fails
 */
export const updateCampaignStatus_api = async (data: { campaignId: string, status: string }) => {
    const { campaignId, status } = data

    try {
        const results = await Axios.put<{ campaign: { status: string } }>(`/campaign/${campaignId}/status`, {
            status
        })

        return results.data
    } catch (error) {
        throw error
    }
}

/**
 * Performs a health check for a specific campaign
 * @param campaignId - The ID of the campaign to health check
 * @param access_token - Optional Facebook access token for authentication
 */
export const healthCheckCampaign_api = async (
    campaignId: number,
    access_token?: string
): Promise<void> => {
    try {
        const response = await Axios.put(`/campaign/${campaignId}/health`,
            access_token ? { access_token } : {}
        );

        if (!response?.data || response.isError) {
            throw new Error('Error performing campaign health check');
        }
    } catch (error) {
        console.error('Failed to perform campaign health check:', error);
        throw error;
    }
}

/** Parameters for updating presave platforms */
interface UpdatePresavePlatformsProps {
    /** Campaign ID to update platforms for */
    campaignId: number;
    /** Array of platform names to enable */
    platforms: string[];
}

/**
 * Updates the enabled platforms for a presave campaign
 * @param props - Parameters for updating presave platforms
 * @returns Promise containing the updated campaign details or throws error
 */
export const updatePresavePlatforms_api = async ({
    campaignId,
    platforms
}: UpdatePresavePlatformsProps): Promise<CampaignDetails> => {
    try {
        const response = await Axios.put<CampaignDetails>(
            `/campaign/presave/${campaignId}/platforms`,
            { platforms }
        );

        if (!response?.data || response.isError) {
            throw new Error(response.message || 'Error updating presave platforms');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update presave platforms:', error);
        throw error;
    }
}

/**
 * Gets the minimal campaign amount allowed for a specific currency
 * @param currency - The currency code to get minimal amount for
 * @returns Promise containing the minimal amount or 0 if error/not found
 */
export const getMinimalAmountRequiredForCurrency_api = async (currency: CurrencyCodesType): Promise<number> => {
    try {
        const response = await Axios.get<{ amount: number }>(
            `/campaign/minimal-amount/${currency}`
        );

        return response.data.amount;
    } catch (error) {
        console.error('Failed to fetch minimal amount:', error);
        return 0;
    }
}

/**
 * Creates a new presave campaign via API
 * @param brandId - ID of the brand creating the campaign
 * @param data - Formatted campaign data to send
 */
export const createPresaveCampaign_api = async (
    brandId: number,
    data: Record<string, any>
): Promise<CampaignPreSaveCreationData | BrandContentExistsData | PresaveCampaignExistsData> => {
    const response = await Axios.post<CampaignPreSaveCreationData | BrandContentExistsData | PresaveCampaignExistsData>(
        `/campaign/brand/${brandId}/presave`,
        data
    );
    return response.data;
}

/**
 * Fetches status information for a playlist campaign
 * @param brandId - ID of the brand associated with the campaign
 * @param campaignId - ID of the campaign to fetch status for
 * @returns Promise containing playlist status data or null if error
 */
export const getPlaylistCampaignStatus_api = async (
    brandId: number | string,
    campaignId: number | string
): Promise<PlaylistDatav2[] | null> => {
    try {
        const response = await Axios.get<PlaylistDatav2[]>(
            `/campaign/brand/${brandId}/${campaignId}/status`
        );

        if (response.isError) {
            throw new Error(response.message || 'Error fetching playlist campaign status');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to fetch playlist campaign status:', error);
        return null;
    }
}

/** Status types for video ad campaigns */
export type VideoAdStatus = 'live' | 'finished' | 'cancelled' | 'pending' | 'in_review' | 'troubleshooting'

/** Video statistics data returned from the API */
export interface VideoStatsData {
    /** Current campaign status */
    status: VideoAdStatus
    /** Total views achieved */
    views: number
    /** Cost per view */
    cpv: number
    /** Total cost spent */
    cost: number
    /** Whether payment was refunded */
    refunded?: boolean
    /** Breakdown of views by location */
    locationsBreakdown?: Array<{
        country: string
        cpv: number
        views: number
        spend: number
    }>
}

/**
 * Fetches status information for either a playlist or video ad campaign
 * @param brandId - ID of the brand associated with the campaign
 * @param campaignId - ID of the campaign to fetch status for
 * @returns Promise containing campaign status data
 */
export const getVideoCampaignStatus_api = async (
    brandId: number | string,
    campaignId: number | string,
): Promise<VideoStatsData | null> => {
    try {
        const response = await Axios.get<VideoStatsData>(
            `/campaign/${campaignId}/analytics`
        );

        if (response.isError) {
            throw new Error(response.message || 'Error fetching campaign status');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to fetch campaign status:', error);
        return null;
    }
}

/** Response type for checkout endpoints */
interface CheckoutResponse {
    /** Whether there was an error during checkout */
    error: boolean;
    /** Checkout URL if successful */
    url?: string;
    /** Additional checkout data */
    /** PayPal specific order details */
    paypalOrder?: {
        id: string;
    };
    /** Campaign ID created during checkout */
    campaignId?: string;
    [key: string]: any;
}

/**
 * Handles checkout process for campaign creation and plan upgrades.
 * Can either redirect to checkout page or return checkout data based on shouldRedirect parameter.
 * 
 * @param currentBrand - The current brand object containing brand details
 * @param data - Checkout data including campaign and billing details
 * @param shouldRedirect - Optional flag to control redirect behavior
 * @returns Object containing checkout URL and error status, or redirects user if shouldRedirect is true
 */
export const redirectToUpgradePlanCheckout_api = async (
    currentBrand: CurrentBrand,
    data: Record<string, unknown>,
    shouldRedirect = true
): Promise<CheckoutResponse> => {
    try {
        const response = await Axios.post<{ url: string; paypalOrder?: { id: string }; campaignId?: string }>(
            `/campaign/brand/${currentBrand.id}/checkout`,
            data
        );

        if (shouldRedirect) {
            window.location.href = response.data.url;
            return { error: false };
        }

        return { ...response.data, error: false };
    } catch (error) {
        Sentry.captureException(error);
        console.error('error: ', error);
        return { error: true }
    }
};

export interface OneOffCampaignCheckoutInput {
    /** The campaign configuration and settings */
    campaign_input: FacebookCampaignInput | PlaylistingCampaignInput | YouTubeCampaignData;
    /** Payment method - currently only supports 'stripe' */
    payment_method: 'stripe';
    /** Source identifier for the checkout */
    checkout_source: string;
}


/**
 * Redirects user to the one-off campaign checkout page.
 * This endpoint is specifically for non-Pro users to create and pay for individual campaigns
 * without requiring a Pro subscription.
 * 
 * @param currentBrand - The current brand object containing brand details
 * @param data - Campaign data including budget, targeting and content details
 * @returns Object with error flag if the request fails
 */
export const createOneOffCampaignCheckout_api = async ({
    campaign_input,
    payment_method,
    checkout_source,
}: OneOffCampaignCheckoutInput) => {
    try {
        const response = await Axios.post<{ url: string }>(
            `/campaign/checkout`,
            {
                campaign_input,
                payment_method,
                checkout_source,
                type: 'one-off'
            }
        );

        window.location.href = response.data.url;
    } catch (error) {
        Sentry.captureException(error);
        console.error("error: ", error);
        return { error: true }
    }
};

/**
 * Creates a campaign and initiates PayPal checkout session.
 * This endpoint creates the campaign draft AND initiates PayPal order creation.
 * 
 * @param params - PayPal checkout request parameters
 * @returns Promise containing PayPal order details
 */
export const createOneOffCampaignPayPalCheckout_v2_api = async ({
    brandId,
    checkout_source,
    campaign_input
}: OneOffCheckoutRequest): Promise<PayPalCheckoutResponse> => {
    try {
        const response = await Axios.post<PayPalCheckoutResponse>(
            `/campaign/checkout/one-off/paypal`,
            {
                brandId,
                checkout_source,
                campaign_input
            }
        );

        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error creating PayPal one-off campaign checkout:', error);
        throw error;
    }
};

/**
 * Redirects user to unlock analytics for an adbox campaign.
 * This endpoint is for non-authenticated users who want to view full analytics
 * for their campaign by paying a one-time fee.
 * 
 * @param campaignId - The campaign ID to unlock analytics for
 * @param source - Source identifier for tracking where the unlock was initiated
 * @returns Object with checkout URL or error flag
 */
export const unlockAdBoxAnalytics_api = async ({
    campaignId,
    source = 'adbox_campaign_details'
}: {
    campaignId: number;
    source?: string;
}) => {
    try {
        const res = await Axios.post<{ url: string }>(
            `/campaign/${campaignId}/adbox/unlock-analytics`,
            {
                source
            }
        );

        return {
            url: res.data.url,
            error: null,
        }
    } catch (error) {
        Sentry.captureException(error);
        console.error("error: ", error);
        return {
            url: null,
            error: true,
        }
    }
};

export interface UpgradePlanCheckoutInput {
    /** The campaign configuration and settings */
    campaign_input: FacebookCampaignInput | TikTokCampaignSubmissionType | YouTubeCampaignData | PlaylistingCampaignInput;
    /** Payment method - currently only supports 'stripe' */
    payment_method: 'stripe';
    /** Source identifier for the checkout */
    checkout_source: string;
    /** Billing cycle for the subscription */
    recurring_interval?: BillingCycle;
}

/**
 * Redirects user to the upgrade plan checkout page to upgrade their account to Pro status.
 * This endpoint handles both upgrading the user's account to Pro and collecting payment details.
 * 
 * @param currentBrand - The current brand object containing brand details
 * @param data - Checkout data including campaign and billing details
 * @returns Object with error flag if the request fails
 */
export const createCampaignAndUpgradePlanCheckout_api = async ({
    campaign_input,
    payment_method,
    recurring_interval,
    checkout_source,
}: UpgradePlanCheckoutInput) => {
    try {
        const response = await Axios.post<{ url: string }>(
            `/campaign/checkout`,
            {
                campaign_input,
                payment_method,
                recurring_interval,
                checkout_source,
            }
        );

        window.location.href = response.data.url;
    } catch (error) {
        Sentry.captureException(error);
        console.error("error: ", error);
        return { error: true }
    }
};

/**
 * Redirects user to the upgrade plan checkout page to upgrade their account to Pro status.
 * This endpoint handles both upgrading the user's account to Pro and collecting payment details.
 * 
 * @param currentBrand - The current brand object containing brand details
 * @param data - Checkout data including campaign and billing details
 * @returns Object with error flag if the request fails
 */
export const createDirectFacebookCampaign_api = async ({
    campaign_input,
    brandId
}: {
    /** The campaign configuration and settings */
    campaign_input: FacebookCampaignSubmissionType | YouTubeCampaignData | PlaylistingCampaignInput
    /** Brand ID */
    brandId: number;
}) => {
    try {
        const response = await Axios.post<{ url: string }>(
            `/campaign/facebook`,
            {
                campaign_input,
                brandId
            }
        );

        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        console.error("error: ", error);
        return { error: true }
    }
};


/** Parameters for updating campaign end date */
interface UpdateCampaignEndDateProps {
    /** Campaign ID to update */
    campaignId: number;
    /** New end date for the campaign */
    endDate: string;
    /** Optional Facebook access token */
    accessToken?: string | null;
}

/**
 * Updates the end date for a campaign
 * @param props - Parameters for updating the campaign end date
 * @returns Promise containing the updated campaign data or throws error
 */
export const updateCampaignEndDate_api = async ({
    campaignId,
    endDate,
    accessToken = null
}: UpdateCampaignEndDateProps): Promise<any> => {
    try {
        const response = await Axios.put<any>(
            `/campaign/${campaignId}/endDate`,
            {
                access_token: accessToken,
                endDate
            }
        );

        if (!response?.data || response.isError) {
            throw new Error('Error updating campaign end date');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update campaign end date:', error);
        throw error;
    }
}

/** Parameters for updating campaign budget and end date */
interface UpdateCampaignBudgetAndEndDateProps {
    /** Campaign ID to update */
    campaignId: number;
    /** New end date for the campaign */
    endDate: string;
    /** New budget amount for the campaign */
    budget: number;
    /** Optional Facebook access token */
    accessToken?: string | null;
}

/**
 * Updates both the budget and end date for a campaign
 * @param props - Parameters for updating the campaign budget and end date
 * @returns Promise containing the updated campaign data
 * @throws Error if the update fails
 */
export const updateCampaignBudgetAndEndDate_api = async ({
    campaignId,
    endDate,
    budget,
    accessToken = null
}: UpdateCampaignBudgetAndEndDateProps): Promise<any> => {
    try {
        const response = await Axios.put(
            `/campaign/${campaignId}/budget-endDate`,
            {
                access_token: accessToken,
                endDate,
                budget
            }
        );

        if (!response?.data || response.isError) {
            throw new Error('Error updating campaign budget and end date');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update campaign budget and end date:', error);
        throw error;
    }
}

/** Parameters for updating presave campaign style */
interface UpdatePresaveStyleProps {
    /** Campaign ID to update */
    campaignId: number;
    /** Style configuration for the presave */
    style: {
        /** Background type (color, artwork, etc) */
        bgType: BackgroundTypes;
        /** Background color */
        bgColor: string;
        /** Button configuration style */
        buttonConfig: StyleTypes;
        /** Primary color for buttons/elements */
        primaryColor: string;
        /** Secondary color for buttons/elements */
        secondaryColor: string;
        /** Title alignment setting */
        titleAlignment: TitleAlignments;
    };
    /** Campaign name/title */
    name: string;
    /** Optional subtitle text */
    subtitle: string;
    /** URL for artwork image */
    artworkImage: string;
    /** Whether to show the secondary line */
    showSecondaryLine: boolean;
    /** Additional campaign options */
    options: {
        /** Whether to redirect to custom webpage */
        redirect_to_custom_webpage: boolean;
        /** Custom webpage URL */
        redirect_url: string;
        [key: string]: any;
    };
}

/**
 * Updates the style and configuration for a presave campaign
 * @param props - Parameters for updating the presave campaign style
 * @returns Promise containing the updated campaign data
 * @throws Error if the update fails
 */
export const updatePresaveStyle_api = async (
    props: UpdatePresaveStyleProps
): Promise<CampaignDetails> => {
    try {
        const response = await Axios.put<CampaignDetails>(
            `/campaign/presave/${props.campaignId}/style`,
            {
                style: props.style,
                name: props.name,
                subtitle: props.subtitle,
                artworkImage: props.artworkImage,
                showSecondaryLine: props.showSecondaryLine,
                options: props.options,
            }
        );

        if (!response?.data || response.isError) {
            throw new Error(response.message || 'Error updating presave campaign style');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update presave campaign style:', error);
        throw error;
    }
}

interface BaseCreateFacebookCampaignProps {
    /** Draft campaign ID if editing existing campaign */
    campaignId?: string;
}
/** Parameters for creating a Facebook campaign */
interface CreateFacebookCampaignProps extends BaseCreateFacebookCampaignProps {
    /** Campaign configuration input */
    campaignInput: FacebookCampaignInput;
}

/** Response data structure for created campaign */
interface CreateCampaignResponse {
    /** Created campaign details */
    campaign: {
        id: string;
        name: string;
        status: string;
        [key: string]: any; // Allow additional campaign properties
    };
    /** Whether organization was successfully upgraded */
    organizationUpgradeSuccess?: boolean;
}

/**
 * Creates or updates a Facebook campaign for a brand
 * @param props - Parameters for creating/updating the campaign
 * @returns Promise containing the created/updated campaign data
 */
export const createFacebookCampaign_api = async ({
    campaignId,
    campaignInput
}: CreateFacebookCampaignProps): Promise<CreateCampaignResponse> => {
    try {
        const response = await Axios.post<CreateCampaignResponse>(
            `/campaign/facebook`,
            {
                campaign_id: campaignId,
                campaign_input: campaignInput
            },
            {
                timeout: 5000
            }
        );

        if (!response?.data) {
            throw new Error('Invalid response from campaign creation');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to create Facebook campaign:', error);
        throw error;
    }
};


/**
 * Creates or updates a Facebook campaign for a brand
 * @param props - Parameters for creating/updating the campaign
 * @returns Promise containing the created/updated campaign data
 */
export const createFacebookCampaign_v2_api = async ({
    brandId,
    campaignInput
}: {
    brandId: number;
    campaignInput: FacebookCampaignSubmissionType;
}): Promise<CreateCampaignResponse> => {
    try {
        const response = await Axios.post<CreateCampaignResponse>(
            `/campaign/facebook`,
            {
                campaign_input: campaignInput,
                brand_id: brandId
            },
            {
                timeout: 5000
            }
        );

        if (!response?.data) {
            throw new Error('Invalid response from campaign creation');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to create Facebook campaign:', error);
        throw error;
    }
};



/**
 * Creates or updates a Facebook campaign for a brand
 * @param props - Parameters for creating/updating the campaign
 * @returns Promise containing the created/updated campaign data
 */
export const createTiktokCampaign_v2_api = async ({
    brandId,
    campaignInput
}: {
    brandId: number;
    campaignInput: TikTokCampaignSubmissionType;
}): Promise<CreateCampaignResponse> => {
    try {
        const response = await Axios.post<CreateCampaignResponse>(
            `/campaign/tiktok`,
            {
                campaign_input: campaignInput,
                brandId: brandId
            },
            {
                timeout: 5000
            }
        );

        if (!response?.data) {
            throw new Error('Invalid response from campaign creation');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to create Facebook campaign:', error);
        throw error;
    }
};


/** Parameters for creating an Instagram growth campaign */
interface CreateGrowInstagramCampaignProps extends BaseCreateFacebookCampaignProps {
    /** Complete campaign configuration */
    campaignInput: GrowInstagramCampaignInput;
}

/**
 * Creates or updates an Instagram growth campaign
 * @param props - Parameters for creating/updating the campaign
 * @returns Promise containing the created/updated campaign data and organization upgrade status
 */
export const createGrowInstagramFacebookCampaign_api = async ({
    campaignId,
    campaignInput
}: CreateGrowInstagramCampaignProps): Promise<CreateCampaignResponse> => {
    try {
        const response = await Axios.post<CreateCampaignResponse>(
            `/campaign/facebook`,
            {
                campaign_id: campaignId,
                campaign_input: campaignInput
            },
            {
                timeout: 5000
            }
        );

        if (!response?.data) {
            throw new Error('Invalid response from campaign creation');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to create Instagram growth campaign:', error);
        throw error;
    }
};

/** Parameters for creating a link clicks campaign */
interface CreateLinkClicksCampaignProps extends BaseCreateFacebookCampaignProps {
    /** Campaign configuration input */
    campaignInput: LinkClicksCampaignInput;
}

/**
 * Creates or updates a Facebook link clicks campaign
 * @param props - Parameters for creating/updating the campaign
 * @returns Promise containing the created/updated campaign data and organization upgrade status
 */
export const createLinkClicksFacebookCampaign_api = async ({
    campaignId,
    campaignInput
}: CreateLinkClicksCampaignProps): Promise<CreateCampaignResponse> => {
    try {
        const response = await Axios.post<CreateCampaignResponse>(
            `/campaign/facebook`,
            {

                campaign_id: campaignId,
                campaign_input: campaignInput
            },
            {
                timeout: 90000
            }
        );

        if (!response?.data) {
            throw new Error('Invalid response from campaign creation');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to create link clicks campaign:', error);
        throw error;
    }
};

/** Response from the video views PayPal checkout endpoint */
interface VideoViewsPayPalCheckoutResponse {
    data: {
        paypalOrder: {
            id: string;
        };
        campaignId: string;
    };
}

/** Result returned from the checkout creation */
interface CreateCheckoutResult {
    orderId: string | null;
    campaignId: string | null;
    error?: boolean;
}

/**
 * Creates a PayPal checkout session for video views campaigns
 * Handles the API interaction and error tracking for video promotion checkouts
 * 
 * @param currentBrand - Brand context for the campaign
 * @param data - Campaign configuration including budget, content and targeting
 * @returns Checkout session details including PayPal order ID and campaign ID
 */
export const createVideoViewsPaypalCheckout_api = async (
    currentBrand: CurrentBrand,
    data: YouTubeCampaignData
): Promise<CreateCheckoutResult> => {
    try {
        const response = await Axios.post<{ paypalOrder: { id: string }; campaignId: string }>(
            `/campaign/brand/${currentBrand.id}/checkout`,
            data
        );

        if (!response?.data) {
            throw new Error('Invalid response from checkout endpoint');
        }

        const { paypalOrder, campaignId } = response.data;

        return {
            orderId: paypalOrder.id,
            campaignId
        };
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error creating video views PayPal checkout:', error);
        return {
            orderId: null,
            campaignId: null,
            error: true
        };
    }
};

/** Props for updating a campaign's budget */
interface UpdateCampaignBudgetProps {
    /** Campaign ID to update */
    campaignId: number;
    /** New budget amount */
    budget: number;
    /** Optional Facebook access token */
    accessToken?: string | null;
}

/**
 * Updates the budget for a campaign
 * @param props - Parameters for updating the campaign budget
 * @returns Promise containing the updated campaign data
 * @throws Error if the update fails
 */
export const updateCampaignBudget_api = async ({
    campaignId,
    budget,
    accessToken = null
}: UpdateCampaignBudgetProps): Promise<any> => {
    try {
        const response = await Axios.put<any>(
            `/campaign/${campaignId}/budget`,
            {
                budget,
                access_token: accessToken
            }
        );

        if (!response?.data) {
            throw new Error('Error updating campaign budget');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update campaign budget:', error);
        throw error;
    }
};

/** Type for location performance statistics */
interface LocationStats {
    clicks: number;
    reach: number;
    impressions: number;
    cpm: number;
    spend: number;
    costPerClick: number;
    costPerConversion: number;
    conversions: number;
}

/** Type for a location detail */
export interface LocationDetail {
    id: number | string;
    name: string;
    path: string[];
    stats: LocationStats;
    status: string;
    canPause: boolean;
    type: string;
    market?: string;
}

/** Type for market performance metrics */
interface MarketPerformance {
    costPerConversion: number;
    reach: number;
    clicks: number;
    views: number;
    impressions: number;
    comments: number;
    shares: number;
    costPerClick: number;
    cpm: number;
    conversions?: number;
}

/** Type for market detail information */
export interface MarketDetail {
    name: string;
    totalBudget: number;
    budgetRemaining: number;
    spend: number;
    status: string;
    performance: MarketPerformance;
}

/**
 * Fetches performance statistics for a specific market
 * @param args.campaignId - ID of the campaign
 * @param args.access_token - Facebook access token
 * @param args.market - Optional market mapping information
 */
export const getMarketDetail_api = async (args: {
    campaignId: number;
    access_token: string;
    market?: CampaignMapping;
}): Promise<MarketDetail | null> => {
    const { campaignId, access_token, market } = args;
    try {
        const url = `campaign/${campaignId}/markets/performance-stats`;
        const response = await Axios.put<MarketDetail>(url, {
            marketId: market?.fbCampaign.id,
            access_token,
        });
        return response.data;
    } catch (error) {
        Logger.error('Error getting the markets performance data');
        return null;
    }
};

/**
 * Fetches location statistics for a specific market
 * @param args.campaignId - ID of the campaign
 * @param args.access_token - Facebook access token
 * @param args.marketId - Optional ID of the market
 */
export const getLocationDetails_api = async (args: {
    campaignId: number;
    access_token: string;
    marketId?: string;
}): Promise<LocationDetail[] | null> => {
    const { campaignId, access_token, marketId } = args;
    try {
        const url = `campaign/${campaignId}/markets/locations-stats`;
        const response = await Axios.put<LocationDetail[]>(url, {
            marketId,
            access_token,
        });
        return response.data;
    } catch (error) {
        Logger.error('Error getting the locations data of market');
        return null;
    }
};

/**
 * Pauses or resumes a market
 * @param args.campaignId - ID of the campaign
 * @param args.access_token - Facebook access token
 * @param args.market - Market mapping information
 */
export const pauseMarket_api = async (args: {
    campaignId: number;
    access_token: string;
    market?: CampaignMapping;
}): Promise<any | null> => {
    const { campaignId, access_token, market } = args;
    const marketId = market?.fbCampaign?.id;
    if (!marketId) return null;
    try {
        const url = `campaign/${campaignId}/${marketId}/pause`;
        const pause = market.fbCampaign.fields.status !== 'PAUSED';
        const response = await Axios.put(url, {
            pause,
            access_token,
        });
        return response.data;
    } catch (error) {
        Sentry.captureException(`Error pausing/resuming location targeting in campaign ${campaignId} -> market: ${marketId}: ${error}`);
        Logger.error('Error pausing selected market for the campaign');
        return null;
    }
};

/**
 * Pauses or resumes a specific location within a market
 * @param args.location - Location detail information
 * @param args.campaignId - ID of the campaign
 * @param args.access_token - Facebook access token
 * @param args.marketId - ID of the market
 */
export const pauseLocation_api = async (args: {
    location: LocationDetail;
    campaignId: number;
    access_token: string;
    marketId?: string;
}): Promise<any | null> => {
    const { location, campaignId, access_token, marketId } = args;
    try {
        const url = `campaign/${campaignId}/markets/${marketId}/pause-location`;
        const pause = location.status !== 'paused';
        const response = await Axios.put(url, {
            target_location: location.id,
            locationType: location.type,
            access_token,
            pause,
        });
        return response.data;
    } catch (error) {
        Sentry.captureException(`Error pausing/resuming location in campaign ${campaignId} -> market: ${marketId}: ${error}`);
        Logger.error('Error pausing selected market for the campaign');
        return null;
    }
};

/**
 * Adds new locations to a market
 * @param args.campaignId - ID of the campaign
 * @param args.locations - Array of locations to add
 * @param args.access_token - Facebook access token
 * @param args.market - Market mapping information
 */
export const addLocationsToMarket_api = async (args: {
    campaignId: number;
    locations: FBLocation[];
    access_token: string;
    market?: CampaignMapping;
}): Promise<any | null> => {
    const { campaignId, locations, access_token, market } = args;
    const marketId = market?.fbCampaign?.id;
    if (!marketId) return null;
    try {
        const url = `campaign/${campaignId}/markets/${marketId}/add-locations`;
        const response = await Axios.put(url, {
            locations,
            access_token,
        });
        return response.data;
    } catch (error) {
        Sentry.captureException(`Error adding location to market: campaign ${campaignId} -> market: ${marketId}: ${error}`);
        Logger.error('Error adding locations to market');
        return null;
    }
};

/**
 * Validates a list of locations for a market
 * @param args.locations - Array of locations to validate
 * @param args.campaignId - ID of the campaign
 * @param args.marketId - ID of the market
 */
export const validateLocations_api = async (args: {
    locations: FBLocation[];
    campaignId: number;
    marketId?: string;
}): Promise<any | null> => {
    const { locations, campaignId, marketId } = args;
    if (!marketId) return null;
    try {
        const url = `campaign/${campaignId}/markets/${marketId}/validate-locations`;
        const response = await Axios.put(url, { locations });
        return response.data;
    } catch (error) {
        Logger.error('Error validating locations');
        return null;
    }
};

/** Type for campaign duplication response */
interface DuplicateCampaignResponse {
    /** The duplicated campaign data */
    campaign: CreatedCampaign;
    /** Additional response data */
    [key: string]: any;
}

/** Parameters for duplicating a campaign */
interface DuplicateCampaignProps {
    /** ID of the campaign to duplicate */
    campaignId: number | string;
    /** Optional project data for the new campaign */
    projectData?: DuplicateSongAdsRecord | string | boolean;
}

/**
 * Duplicates an existing campaign with optional new project data
 * @param props - Parameters for duplicating the campaign
 * @returns Promise containing the duplicated campaign data or null if error
 */
export const duplicateCampaign_api = async ({
    campaignId,
    projectData
}: DuplicateCampaignProps): Promise<DuplicateCampaignResponse | null> => {
    try {
        const response = await Axios.put<DuplicateCampaignResponse>(
            `/campaign/${campaignId}/duplicate/v2`,
            { projectData }
        );

        return response.data;
    } catch (error) {
        console.error('Failed to duplicate campaign:', error);
        return null;
    }
}


/**
 * Archives a campaign by setting its status to ARCHIVED
 * @param props - Parameters for archiving the campaign
 * @returns Promise containing the archived campaign data or null if error
 */
export const archiveCampaign_api = async ({
    campaignId,
}: {
    campaignId: number;
}): Promise<CampaignDetails> => {
    try {
        const response = await Axios.put<CampaignDetails>(
            `/campaign/${campaignId}/archive`
        );

        if (!response?.data || response.isError) {
            throw new Error('Error archiving campaign');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to archive campaign:', error);
        throw error;
    }
}

/**
 * Unarchives a campaign by restoring its previous status
 * @param props - Parameters for unarchiving the campaign
 * @returns Promise containing the unarchived campaign data or null if error
 */
export const unarchiveCampaign_api = async ({
    campaignId,
}: {
    campaignId: number;
}): Promise<CampaignDetails> => {
    try {
        const response = await Axios.put<CampaignDetails>(
            `/campaign/${campaignId}/unarchive`
        );

        if (!response?.data || response.isError) {
            throw new Error('Error unarchiving campaign');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to unarchive campaign:', error);
        throw error;
    }
}

export const renameCampaign_api = async ({
    campaignId,
    newName,
}: {
    campaignId: number;
    newName: string;
}) => {
    try {
        const response = await Axios.put(
            `/campaign/${campaignId}/rename`,
            { newName }
        );

        return response.data;
    } catch (error) {
        console.error('Failed to rename campaign:', error);
        return null;
    }
}

/**
 * Duplicates an existing campaign with optional new project data
 * @param props - Parameters for duplicating the campaign
 * @returns Promise containing the duplicated campaign data or null if error
 */
export const adminRetryCampaign_api = async ({
    campaignId,
    socialPlatform = 'facebook',
}: {
    campaignId: number;
    socialPlatform?: 'facebook' | 'tiktok' | 'playlisting';
}): Promise<CampaignDetails | null> => {
    try {
        const response = await Axios.post<CampaignDetails>(
            `/campaign/${socialPlatform}/${campaignId}/retry/v2`,
        );

        return response.data;
    } catch (error) {
        console.error('Failed to retry campaign:', error);
        return null;
    }
}


/** Interface for email template data */
export interface EmailTemplateData {
    /** Whether to send immediately or schedule for later */
    asap: boolean;
    /** Content details for the email */
    content: {
        /** HTML content/body of the email */
        html_content: string;
        /** Subject line of the email */
        subject_line: string;
    };
    /** Date information for scheduled emails */
    date?: {
        /** UTC formatted release date */
        release_date?: string;
        /** Timezone for scheduling */
        timezone?: string;
    };
    /** Email fanbase targeting information */
    email_fanbase: {
        /** List of target email addresses */
        targets: string[];
        /** Total number of recipients */
        sentTo: number;
    };
}

/**
 * Sends email template data for a brand campaign
 * @param brandId - ID of the brand sending the email
 * @param dataToSend - Email template data to send
 * @returns Promise containing response data or error
 */
export const sendEmailTemplateData_api = async (
    brandId: number,
    emailTemplateData: EmailTemplateData
): Promise<EmailCampaignData> => {
    const response = await Axios.post<EmailCampaignData>(
        `/brand/${brandId}/email-template`,
        emailTemplateData
    );
    return response.data;
};

/**
 * Retrieves details for a specific campaign
 * @param campaignId - ID of the campaign to fetch details for
 * @returns Promise containing campaign details
 */
export const getEmailCampaignDetails_api = async (
    campaignId: number
): Promise<EmailCampaignDetailsData | null> => {
    try {
        const response = await Axios.get<EmailCampaignDetailsData>(`/campaign/${campaignId}/email`);
        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};

/** Interface for campaign recommendation response */
export interface MaestroRecommendation {
    /** Primary message from the recommendation engine */
    primaryMessage: string;
    /** Array of specific recommendations with details */
    recommendations: {
        /** Unique identifier for the recommendation */
        key: string;
        /** Title of the recommendation */
        title: string;
        /** Detailed message/description */
        message: string;
        /** Related resource link */
        link: string;
    }[] | null;
}

/**
 * Fetches AI-powered campaign recommendations based on request type
 * @param type - Type of campaign recommendation to request
 * @returns Promise containing recommendation data
 */
export const getCampaignRecommendations_api = async (
    type: string
): Promise<MaestroRecommendation> => {
    try {
        const response = await Axios.post<MaestroRecommendation>('/campaign/recommendations', {
            request: type
        });

        if (response.data) {
            return response.data;
        }

        return {
            primaryMessage: 'There was an error processing your request. Please try re-asking!',
            recommendations: null
        };
    } catch (error) {
        Sentry.captureException(error);
        return {
            primaryMessage: 'There was an error processing your request. Please try re-asking!',
            recommendations: null
        };
    }
};

/**
 * Fetches creative performance data for a campaign
 * @param params - Object containing required parameters
 * @param params.campaignId - ID of the campaign to fetch performance data for
 * @param params.access_token - Facebook access token for authentication
 * @param params.brandId - ID of the brand associated with the campaign
 * @returns Promise containing creative performance data or undefined if error
 */
export const getCreativePerformanceData_api = async ({
    campaignId,
    brandId,
    access_token
}: {
    campaignId: number;
    brandId: number | undefined;
    access_token?: string;
}) => {
    try {
        if (!brandId) {
            return
        }

        const response = await Axios.put(`campaign/${campaignId}/creative-performance`, {
            brandId
        });
        return response.data;
    } catch (e) {
        Logger.error('Error getting creative performance data');
    }
};

interface SectionStatsGroup {
    name: string;
    subtitle: string;
    thumbnail?: string;
    canPause: boolean;
    stats: Array<StatItem>;
}

/** Facebook Action Type interface for raw action data */
interface FacebookActionType {
    action_type: string;
    value: string | number;
}

export interface GeographicBreakdownItem {
    // Geographic identifiers - varies by breakdown type
    country?: string | null;
    countryName?: string | null;
    region?: string | null;
    dma?: string | null;
    
    // Processed numeric fields
    cpc: number;
    cpm: number;
    reach: number;
    impressions: number;
    spend: number;
    inline_link_clicks: number;
    clicks: number;
    cost_per_inline_link_click: number;
    
    // Processed conversion metrics
    totalConversions: number;
    costPerConversion: number;
    
    // Processed engagement metrics from actions
    likes: number;
    comments: number;
    shares: number;
    
    // Keep conversion and action arrays for full data access
    actions: FacebookActionType[];
    conversions: FacebookActionType[];
    cost_per_conversion: FacebookActionType[];
    cost_per_action_type: FacebookActionType[];
    outbound_clicks: FacebookActionType[];
    purchase_roas: FacebookActionType[];
}

export interface DemographicsBreakdownItem {
    age: string;
    gender: string;
    
    // Basic performance metrics
    reach: number;
    cpc: number;
    impressions: number;
    clicks: number;
    spend: number;
    inline_link_clicks: number;
    cost_per_inline_link_click: number;
    
    // Processed conversion metrics
    totalConversions: number;
    costPerConversion: number;
    
    // Processed engagement metrics from actions
    likes: number;
    comments: number;
    shares: number;
    
    // Keep conversion and action arrays for full data access
    actions: FacebookActionType[];
    conversions: FacebookActionType[];
    cost_per_conversion: FacebookActionType[];
    cost_per_action_type: FacebookActionType[];
    outbound_clicks: FacebookActionType[];
    purchase_roas: FacebookActionType[];
}


/**
 * Interface for asset performance analytics result
 */
export interface AssetPerformanceAnalytics {
    /** Asset ID from the creative */
    assetId: string;
    /** Thumbnail URL for the asset */
    thumbnailUrl: string;
    /** Original post type */
    originalPostType: AssetPlatformType;
    /** Media type of the post */
    mediaType: MediaType;
    /** URL to the actual media asset */
    assetUrl: string;
    /** Preview link for the asset */
    previewLink: string;
    /** Whether the asset is ineligible */
    isIneligible?: boolean;
    /** Reason for ineligibility */
    ineligible_reason?: string;
    /** Permalink URL for the asset on platform */
    permalinkUrl?: string;
    statsArray: Array<{
        name: string;
        value: number;
        displayType: string | 'currency' | 'percentage' | 'number' | 'default';
    }>;
    /** Performance statistics for this asset */
    stats: {
        /** Number of impressions */
        impressions: number;
        /** Number of reach */
        reach: number;
        /** Number of clicks */
        clicks: number;
        /** Cost per click */
        cpc: number;
        /** Number of inline link clicks */
        inlineClicks: number;
        /** Total amount spent on ads with this asset */
        spend: number;
        /** Conversion data if available */
        conversions?: number;
        /** Cost per conversion if available */
        costPerConversion?: number;
    };
}



/**
 * Main interface for campaign details data
 */
export interface CampaignDetailsData {
    campaignStatus: string;
    campaignStatusNote?: string;
    campaignType: string;
    budget: {
        total: number;
        spent: number;
        remaining: number;
    };
    campaignLength: {
        startDate: string;
        endDate: string;
        totalDays: number;
        remainingDays: number;
    };
    geographicBreakdown: {
        countries: Array<GeographicBreakdownItem>;
        regions: Array<GeographicBreakdownItem>;
        dmas: Array<GeographicBreakdownItem>;
    };
    demographicsBreakdown: Array<DemographicsBreakdownItem>;
    platformClicksBreakdown: StreamingPlatformsData;
    dailyStats: Array<{
        date: string;
        totalSpend: number;
        totalReach: number;
        totalImpressions: number;
        totalClicks: number;
        clicks: string;
        inlineClicks: number;
        cpic: number;
        cpc: number;
        spend: number;
        costPerConversion: number;
        conversions: number;
    }>;
    topLevelPerformanceStats: {
        primaryStats: Array<{
            name: string;
            subtitle: string;
            stats: Array<StatItem>;
        }>;
        secondaryStats: Array<StatItem>;
    };
    markets: Array<{
        id: string;
        name: string;
        budget: number;
        spent: number;
        status: string;
        canPause: boolean;
    }>;
    errors: Array<{
        title: string;
        subtitle: string;
        actions: Array<{
            title: string;
            onClick: string;
        }>;
    }>;
    creativePerformanceStats: Array<StatItem>;
    audienceStats: SectionStatsGroup[];
    creativeStats: SectionStatsGroup[];
    assetPerformanceAnalytics: AssetPerformanceAnalytics[];
}

export interface PresaveCampaignPresaveStatus {
    status?: 'pending' | 'in-progress' | 'completed';
    overall: {
        expected: number;
        saved: number;
        remaining: number;
        failed: number;
    }
    presaves?: {
        total: number;
        completed: number;
        pending: number;
        failed: number;
    };
    foreverSaves?: {
        expected: number;
        saved: number;
        remaining: number;
        failed: number;
    };
}
export const getPresaveCampaignPresaveStatus_api = async ({
    campaignId
}: {
    campaignId: number
}): Promise<PresaveCampaignPresaveStatus> => {
    const response = await Axios.get(`campaign/${campaignId}/presave-status`);
    return response.data;
}

export const getPresaveCampaignAnalytics_api = async ({
    campaignId
}: {
    campaignId: number
}): Promise<PresaveCampaignPresaveStatus> => {
    const response = await Axios.get(`campaign/${campaignId}/presave-analytics`);
    return response.data;
}

export const retryApplyPresaves_api = async ({
    campaignId
}: {
    campaignId: number
}): Promise<PresaveCampaignPresaveStatus> => {
    const response = await Axios.post(`campaign/${campaignId}/retry-apply-presaves`);
    return response.data;
}

/**
 * Fetches comprehensive campaign analytics data
 * @param params - Object containing required parameters
 * @param params.campaignId - ID of the campaign
 * @returns Promise containing campaign analytics data
 */
export const getCampaignAnalyticsV2_api = async ({
    campaignId,
}: {
    campaignId: number;
}): Promise<CampaignDetailsData | undefined> => {
    try {
        let url = `campaign/${campaignId}/analytics`;

        const response = await Axios.get<CampaignDetailsData>(url, { timeout: 60 * 1000 });
        return response.data;
    } catch (e) {
        Logger.error('Error fetching campaign analytics data');
        throw e;
    }
};

/** Type for individual daily statistics entry */
export interface DailyStat {
    /** Date of the statistic in YYYY-MM-DD format */
    datetime: string;
    /** Value for that day */
    value: number;
    /** Net change over the time period */
    absoluteChange?: number;
    /** Net change in absolute value */
    percentageChange?: number;
}

/** Type for campaign metric data */
interface MetricData {
    /** Array of daily statistics */
    dailyStats: DailyStat[];
    /** Percentage change over the time period */
    percentageChange: number;
    /** Net change in absolute value */
    netChange: number;
    /** Current total value */
    currentValue: number;
    /** Title for the secondary statistic */
    secondaryStatTitle: string;
    /** Value for the secondary statistic */
    secondaryStatValue: number;
}

/** Type for a campaign metric card */
export interface CampaignImpactStatsMetric {
    /** Title of the metric */
    title: string;
    /** Icon identifier for the metric */
    icon: string;
    /** Detailed metric data */
    data: MetricData;
}

/**
 * Fetches comprehensive campaign analytics data
 * @param params - Object containing required parameters
 * @param params.campaignId - ID of the campaign
 * @returns Promise containing campaign analytics data
 */
export const getCampaignAnalytics_impactStats_v2_api = async ({
    campaignId,
}: {
    campaignId: number;
}): Promise<CampaignImpactStatsMetric[] | undefined> => {
    try {
        let url = `campaign/${campaignId}/analytics/impact-stats`;

        const response = await Axios.get<CampaignImpactStatsMetric[]>(url, { timeout: 60 * 1000 });
        return response.data;
    } catch (e) {
        Logger.error('Error fetching campaign analytics data');
        throw e;
    }
};

/** Parameters for fetching Spotify streams data */
interface GetSpotifyStreamsProps {
    /** Brand slug identifier */
    brandSlug: string;
    /** Content slug identifier */
    contentSlug: string;
    /** Start date in YYYY-MM-DD format */
    startDate: string;
    /** End date in YYYY-MM-DD format */
    endDate: string;
}

/**
 * Fetches Spotify streams data for a specific content within a date range
 * @param props - Parameters for fetching Spotify streams data
 * @returns Promise containing streams data or null if error
 */
export const getSpotifyStreams_api = async ({
    brandSlug,
    contentSlug,
    startDate,
    endDate
}: GetSpotifyStreamsProps): Promise<StreamsData | null> => {
    try {
        const res = await Axios.get<StreamsData>(
            `dashboard/brand/${brandSlug}/content/${contentSlug}/spotify/streams`,
            {
                params: {
                    startDate,
                    endDate
                }
            }
        );

        return res.data;
    } catch (error) {
        console.error('Failed to fetch Spotify streams:', error);
        return null;
    }
}


export const getCampaignMaestroRecommendations_api = async ({
    campaignId
}: {
    campaignId: number
}) => {
    try {
        const res = await Axios.get(`campaign/${campaignId}/maestro-recommendations`);
        return res.data;
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
}

/**
 * Fetches campaign data for a specific campaign ID in the fans section
 * @param campaignId - ID of the campaign to fetch
 * @returns Promise containing campaign data or null if error occurs
 */
export const getFansCampaign_api = async (campaignId: number) => {
    try {
        const response = await Axios.get(`/fans/campaigns/${campaignId}`);
        return {
            data: response.data,
            error: null
        };
    } catch (error) {
        console.error('Failed to fetch fans campaign:', error);
        return {
            data: null,
            error
        };
    }
}


/**
 * Interface for the pause/resume asset API parameters
 */
interface PauseResumeAssetProps {
    /** The campaign ID */
    campaignId: number;
    /** The creative ID of the asset to pause/resume */
    creativeId: string;
    /** Whether to pause (true) or resume (false) the asset */
    pause: boolean;
}

/**
 * Pauses or resumes specific ad assets in a campaign
 * @param props - Parameters for pausing/resuming an asset
 * @returns Promise containing the updated campaign data or throws error
 */
export const pauseResumeAsset_api = async ({
    campaignId,
    creativeId,
    pause,
}: PauseResumeAssetProps): Promise<CampaignDetails> => {
    try {
        const response = await Axios.put<CampaignDetails>(
            `/campaign/${campaignId}/asset/pause`,
            {
                creativeId,
                pause
            },
            { timeout: 30000 }
        );

        if (!response?.data || response.isError) {
            throw new Error('Error updating asset pause status');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to update asset pause status:', error);
        throw error;
    }
}

/**
 * Upload new assets to an existing campaign
 * @param campaignId - ID of the campaign to add assets to
 * @param assets - Array of assets to upload
 * @returns Promise containing the updated campaign data or throws error
 */
export const uploadNewAssets_api = async (
    campaignId: number,
    assets: CreativeSelectorPost[]
): Promise<CampaignDetails> => {
    try {
        const response = await Axios.post<CampaignDetails>(
            `/campaign/${campaignId}/assets/upload`,
            { assets },
            { timeout: 60000 } // Longer timeout for uploads
        );

        if (!response?.data || response.isError) {
            throw new Error('Error uploading new assets');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to upload new assets:', error);
        throw error;
    }
};


export interface AdBoxResponse {
    url: string | null;
    message: string;
    error: boolean;
}

/**
 * Creates a campaign through the AdBox endpoint (no authentication required)
 * @param campaign_input - Campaign configuration
 * @param variant - Pricing variant ('take-rate' or 'automation-fee')
 * @returns Response with Stripe checkout URL or error
 */
export const createAdBoxCampaign_api = async ({
    campaign_input
}: {
    campaign_input: FacebookCampaignSubmissionType;
}): Promise<AdBoxResponse> => {
    try {
        const response = await Axios.post<AdBoxResponse>(
            '/campaign/facebook/adbox',
            {
                campaign_input,
                checkout_source: 'campaign-creation',
            }
        );

        return response.data;
    } catch (error: any) {
        Logger.error('Failed to create AdBox campaign:', error);
        return {
            url: null,
            message: error?.response?.data?.message || 'Failed to create campaign',
            error: true
        };
    }
}

/** Request parameters for getting automation fee calculation */
export interface AutomationFeeRequest {
    /** Ad platform type */
    adPlatform: 'META' | 'YOUTUBE' | 'TIKTOK' | 'PLAYLISTS';
    /** Campaign budget amount */
    budget: number;
    /** Currency code - defaults to 'USD' */
    currency?: string;
    /** Whether the campaign uses express setup - defaults to false */
    usesExpressSetup?: boolean;
    /** Playbook type - backend determines playlisting vs automation fee logic */
    playbookType?: string;
}

/** Response data for automation fee calculation */
export interface AutomationFeeResponse {
    /** Total amount customer pays */
    totalCost: number;
    /** Amount that goes to actual ads */
    adsBudget: number;
    /** Automation fee amount */
    takeRate: number;
    /** Detailed breakdown of line items */
    lineItems: {
        /** Display name for the line item */
        name: string;
        /** Description of what this line item covers */
        description: string;
        /** Amount for this line item */
        amount: number;
        /** Currency code */
        currency: string;
        /** Type of line item */
        type: 'marketing_budget' | 'automation_fee';
    }[];
}

/** Request body for one-off checkout */
export interface OneOffCheckoutRequest {
    /** Brand ID */
    brandId: number;
    /** Source identifier for tracking */
    checkout_source?: string | null;
    /** Campaign configuration */
    campaign_input: FacebookCampaignSubmissionType | 
                    TikTokCampaignSubmissionType | 
                    YouTubeCampaignData | 
                    PlaylistingCampaignInput;
}

/** Response from one-off checkout */
export interface OneOffCheckoutResponse {
    /** Stripe checkout session URL */
    url: string;
}

/** Response from PayPal one-off checkout */
export interface PayPalCheckoutResponse {
    /** PayPal order ID */
    paypalOrderId: string;
    /** PayPal approval URL for user to complete payment */
    approvalUrl: string;
    /** Campaign information */
    campaign: {
        id: number;
    };
}

/**
 * Reverts a campaign from AWAITING_PAYMENT status back to DRAFT
 * Used when PayPal checkout is cancelled or fails
 * @param campaignId - The ID of the campaign to revert
 * @returns Promise resolving to updated campaign data
 */
export const revertCampaignFromAwaitingPayment_api = async (campaignId: number): Promise<any> => {
    try {
        const response = await Axios.put(`/campaign/${campaignId}/revert-from-awaiting-payment`);
        return response.data;
    } catch (error) {
        console.error('Failed to revert campaign from awaiting payment:', error);
        throw error;
    }
};

/**
 * Captures a PayPal order and finalizes the campaign
 * Used after user approves PayPal payment to complete the transaction
 * @param orderId - The PayPal order ID to capture
 * @returns Promise resolving to capture response data
 */
export const capturePayPalOrder_api = async (orderId: string): Promise<any> => {
    try {
        const response = await Axios.post(`/campaign/${orderId}/paypal/capture`);
        return response.data;
    } catch (error) {
        console.error('Failed to capture PayPal order:', error);
        throw error;
    }
};

/**
 * Get automation fee calculation for a campaign
 * @param brandId - Brand ID to calculate fees for
 * @param params - Fee calculation parameters
 * @returns Promise containing automation fee details or throws error
 */
export const getAutomationFee_api = async (
    brandId: number,
    params: AutomationFeeRequest
): Promise<AutomationFeeResponse> => {
    try {
        const response = await Axios.post<AutomationFeeResponse>(
            `/campaign/checkout/one-off/get-automation-fee`,
            { brandId, ...params }
        );

        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error getting automation fee:', error);
        throw error;
    }
};

/**
 * Creates a campaign after successful payment intent processing.
 * This endpoint handles creating campaigns for all ad platforms after payment has been confirmed.
 * 
 * @param data - Campaign creation data including campaign input and brand ID
 * @returns Object with campaign data or error flag if the request fails
 */
export const createCampaignAfterPaymentIntent_api = async ({
    campaign_input,
    brandId
}: {
    /** The campaign configuration and settings */
    campaign_input: FacebookCampaignSubmissionType | TikTokCampaignSubmissionType | YouTubeCampaignData | PlaylistingCampaignInput | PlaylistingCampaignSubmissionType
    /** Brand ID */
    brandId: number;
}) => {
    try {
        const response = await Axios.post<{ data: CampaignDetails; message: string; error: boolean }>(
            `/campaign/payment-intent`,
            {
                campaign_input,
                brandId
            }
        );

        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        console.error("error: ", error);
        return { error: true }
    }
};

/**
 * Imports multiple Instagram posts from URLs.
 * This endpoint will search for posts in the connected Instagram account first,
 * and if not found, will import them directly.
 * 
 * @param urls - Array of Instagram post URLs to import
 * @param brandId - Brand ID for the import operation
 * @returns Object with Instagram posts (found in account) and imported posts (from URLs)
 */
export const importMultipleInstagramPosts_api = async ({
    urlString,
    brandId
}: {
    urlString: string;
    brandId: number;
}): Promise<{
    instagramPosts?: InstagramPost[];
    importedPosts?: ImportFromURLResponse[];
    errors?: string[];
    message?: string;
}> => {
    try {
        const response = await Axios.post<{
            instagramPosts?: InstagramPost[];
            importedPosts?: ImportFromURLResponse[];
            errors?: string[];
            message?: string;
        }>('/campaign/import-multiple-posts', {
            urlString,
            brandId
        });

        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error importing multiple Instagram posts:', error);
        throw error;
    }
};

/**
 * Triggers campaign processing via CampaignExecutor for AWAITING_AGENT_PROCESSING campaigns
 * @param campaignId - The ID of the campaign to process
 * @param brandId - Optional brand ID for validation
 * @returns Promise containing the processing result
 */
export const processAgentCampaign_api = async ({
    campaignId,
    brandId
}: {
    campaignId: number;
    brandId?: number;
}): Promise<{
    success: boolean;
    campaignId: string;
    status: string;
    message: string;
    executionId: string;
    error?: string;
}> => {
    try {
        const response = await Axios.post<{
            success: boolean;
            campaignId: string;
            status: string;
            message: string;
            executionId: string;
            error?: string;
        }>(`/agents/campaign-executor/${campaignId}/process`, {
            brandId
        });

        return response.data;
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error processing campaign:', error);
        throw error;
    }
};

/** Parameters for extending a finished campaign */
interface ExtendCampaignProps {
    /** Campaign ID to extend */
    campaignId: number;
    /** New end date for the campaign */
    newEndDate: string;
    /** New budget amount for the campaign */
    newBudget: number;
}

/**
 * Extends a finished campaign with a new end date and budget
 * @param props - Parameters for extending the campaign
 * @returns Promise containing the updated campaign data or throws error
 */
export const extendCampaign_api = async ({
    campaignId,
    newEndDate,
    newBudget
}: ExtendCampaignProps): Promise<any> => {
    try {
        const response = await Axios.put<any>(
            `/campaign/${campaignId}/extend`,
            {
                newEndDate,
                newBudget
            }
        );

        if (!response?.data || response.isError) {
            throw new Error('Error extending campaign');
        }

        return response.data;
    } catch (error) {
        console.error('Failed to extend campaign:', error);
        throw error;
    }
};